/*
Copyright 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-NevaiMeet-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, useCallback, useState } from "react";
import { IconButton, Tooltip } from "@vector-im/compound-web";
import { useTranslation } from "react-i18next";

import FullScreenMaximiseIcon from "../icons/FullScreenMaximise.svg?react";
import FullScreenMinimiseIcon from "../icons/FullScreenMinimise.svg?react";

export const FullscreenButton: FC = () => {
  const { t } = useTranslation();
  const [isFull, setIsFull] = useState<boolean>(() => !!document.fullscreenElement);

  const toggle = useCallback(() => {
    const root = document.body;
    if (!root) return;
    if (document.fullscreenElement) {
      void document.exitFullscreen();
      setIsFull(false);
    } else {
      void root.requestFullscreen();
      setIsFull(true);
    }
  }, []);

  const label = isFull
    ? t("action.exit_full_screen", "Exit full screen")
    : t("action.enter_full_screen", "Enter full screen");

  return (
    <Tooltip label={label}>
      <IconButton aria-label={label} onClick={toggle}>
        {isFull ? (
          <FullScreenMinimiseIcon aria-hidden />
        ) : (
          <FullScreenMaximiseIcon aria-hidden />
        )}
      </IconButton>
    </Tooltip>
  );
};
